import React, { useState, useEffect } from 'react';
import { ExternalLink, CheckCircle, Bookmark, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';

export default function ReEstimatePanel({ teamId, jiraProjectKey, cloudName }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState({});
  const [baselinePoints, setBaselinePoints] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.jira.reestimate(jiraProjectKey);
      setTickets(data);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (jiraProjectKey) load(); }, [jiraProjectKey]);

  const markReviewed = async (key) => {
    setActing(a => ({ ...a, [key]: 'reviewing' }));
    try {
      await api.jira.markReviewed(key);
      setTickets(ts => ts.filter(t => t.key !== key));
    } catch (e) { setError(e.message); } finally { setActing(a => ({ ...a, [key]: null })); }
  };

  const markBaseline = async (ticket) => {
    const points = baselinePoints[ticket.key];
    if (!points) { setError('Enter story points before marking as baseline.'); return; }
    setActing(a => ({ ...a, [ticket.key]: 'baselining' }));
    try {
      await api.jira.markBaseline(ticket.key, {
        team_id: teamId, jira_ticket_summary: ticket.summary, points: parseInt(points)
      });
      setTickets(ts => ts.filter(t => t.key !== ticket.key));
    } catch (e) { setError(e.message); } finally { setActing(a => ({ ...a, [ticket.key]: null })); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 style={{ fontSize: '1rem' }}>Re-estimate Tickets</h3>
          <p className="text-xs text-muted mt-1">Tickets tagged <code className="font-mono" style={{ fontSize: 10 }}>re-estimate</code> in Jira. Review, confirm, or set as baseline.</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14} /></button>
      </div>

      {error && <div className="toast toast-error mb-3">{error}<button className="btn btn-ghost btn-sm" onClick={() => setError('')}>✕</button></div>}

      {loading && <p className="text-sm text-muted pulse">Loading from Jira...</p>}

      {!loading && tickets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
          <p className="text-muted text-sm">No re-estimate tickets. All clear.</p>
        </div>
      )}

      <div className="flex-col gap-3">
        {tickets.map(t => (
          <div key={t.key} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--yellow)', borderRadius: 'var(--radius-sm)', padding: '12px 16px' }}>
            <div className="flex items-start gap-3">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="badge badge-yellow font-mono">{t.key}</span>
                  {t.currentPoints != null && <span className="badge badge-purple">{t.currentPoints} pts</span>}
                  {t.priority && <span className="text-xs text-muted">{t.priority}</span>}
                </div>
                <p className="text-sm">{t.summary}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="badge badge-muted">{t.status}</span>
                  <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-accent flex items-center gap-1">
                    Jira <ExternalLink size={10} />
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <button className="btn btn-success btn-sm" onClick={() => markReviewed(t.key)} disabled={!!acting[t.key]}>
                <CheckCircle size={12} /> {acting[t.key] === 'reviewing' ? 'Marking...' : 'Mark Reviewed'}
              </button>
              <div className="flex items-center gap-1">
                <select className="input" value={baselinePoints[t.key] || ''} onChange={e => setBaselinePoints(p => ({ ...p, [t.key]: e.target.value }))}
                  style={{ width: 80, padding: '5px 8px', fontSize: 12 }}>
                  <option value="">pts</option>
                  {['1','2','3','5','8'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <button className="btn btn-sm" style={{ background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent)' }}
                  onClick={() => markBaseline(t)} disabled={!!acting[t.key] || !baselinePoints[t.key]}>
                  <Bookmark size={12} /> {acting[t.key] === 'baselining' ? 'Saving...' : 'Set Baseline'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
